"use client";
import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Stack,
  Snackbar,
  Fade,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UserInfo from "../UserManagement/EditUser/UserInfo";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import { AuthContext } from "../../../authentication/AuthContext";
import { triggerProfileRefresh } from "../../../hooks/useProfileRefresh";

function CustomTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 3 }}>{children}</Box> : null;
}

const EditProfile = () => {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);

  // Get admin_user_id from localStorage
  const [admin_user_id, setAdminUserId] = useState(null);

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [profilePicture, setProfilePicture] = useState(null);

  // Original values for cancel functionality
  const [originalValues, setOriginalValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    role: "user",
    profilePicture: null
  });

  // UI states
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  const handleChange = (event, newValue) => setValue(newValue);

  // Get admin_user_id from localStorage on mount
  useEffect(() => {
    try {
      const adminUserData = localStorage.getItem("adminUserData");
      if (adminUserData) {
        const parsedData = JSON.parse(adminUserData);
        if (parsedData.admin_user_id) {
          setAdminUserId(parsedData.admin_user_id);
        } else {
          setSnackbarMsg("Unable to load user profile");
          setSnackbarOpen(true);
          setFetching(false);
        }
      } else {
        setSnackbarMsg("User data not found");
        setSnackbarOpen(true);
        setFetching(false);
      }
    } catch (err) {
      setSnackbarMsg("Error loading user data");
      setSnackbarOpen(true);
      setFetching(false);
    }
  }, []);

  // Fetch user data when admin_user_id is available
  useEffect(() => {
    if (!admin_user_id) return;

    const fetchUserDetails = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL_V1}/admin/user/details/${admin_user_id}`, {
          headers: config.getHeaders()
        });
        const json = await res.json();
        if (res.ok && json?.data) {
          const u = json.data;
          const firstName_val = u.first_name || "";
          const lastName_val = u.last_name || "";
          const email_val = u.email_id || "";
          const phoneNumber_val = u.phone_number || "";
          const password_val = "";
          const role_val = u.role || "user";

          let picture_val = null;
          if (u.profile_picture_url) {
            const pictureUrl = u.profile_picture_url.startsWith('/')
              ? `${config.API_BASE_URL_V1}${u.profile_picture_url}`
              : u.profile_picture_url;
            picture_val = pictureUrl;
          }

          // Set form values
          setFirstName(firstName_val);
          setLastName(lastName_val);
          setEmail(email_val);
          setPhoneNumber(phoneNumber_val);
          setPassword(password_val);
          setRole(role_val);
          setProfilePicture(picture_val);

          // Store original values for cancel functionality
          setOriginalValues({
            firstName: firstName_val,
            lastName: lastName_val,
            email: email_val,
            phoneNumber: phoneNumber_val,
            password: password_val,
            role: role_val,
            profilePicture: picture_val
          });
        } else {
          setSnackbarMsg(json?.message || "Failed to load profile");
          setSnackbarOpen(true);
        }
      } catch (err) {
        setSnackbarMsg("Error fetching profile details");
        setSnackbarOpen(true);
      } finally {
        setFetching(false);
      }
    };

    fetchUserDetails();
  }, [admin_user_id]);

  const handleCancel = () => {
    // Reset all form fields to original values
    setFirstName(originalValues.firstName);
    setLastName(originalValues.lastName);
    setEmail(originalValues.email);
    setPhoneNumber(originalValues.phoneNumber);
    setPassword(originalValues.password);
    setRole(originalValues.role);
    setProfilePicture(originalValues.profilePicture);
  };

  const handleSave = async () => {
    if (!admin_user_id) {
      setSnackbarMsg("User ID not found");
      setSnackbarOpen(true);
      return;
    }

    // validations handled by UserInfo and isFormValid state
    if (!isFormValid) {
      setSnackbarMsg("Please fix validation errors before saving.");
      setSnackbarOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append("first_name", firstName.trim());
    formData.append("last_name", lastName.trim());
    formData.append("email_id", email.trim());
    formData.append("phone_number", phoneNumber.trim());
    if (password) formData.append("password", password);

    // Only append a file if the user selected a new one (strings are existing URLs)
    if (profilePicture && typeof profilePicture !== "string") {
      formData.append("profile_picture", profilePicture);
    }

    try {
      setLoading(true);
      const res = await fetch(`${config.API_BASE_URL_V1}/admin/user/update/${admin_user_id}`, {
        method: "POST",
        headers: config.getHeaders(),
        body: formData,
      });

      const json = await res.json();

      if (!res.ok || json?.success === false) {
        let errMsg = json?.message || "Failed to update profile";

        if (json?.error?.details && json.error.details.includes("email_id_unique")) {
          errMsg = `${errMsg} - Email already exists`;
        }

        setSnackbarMsg(errMsg);
        setSnackbarOpen(true);
        return;
      }

      setSnackbarMsg(json?.message || "Profile updated successfully.");
      setSnackbarOpen(true);

      // If updating current user, refresh profile in real-time
      if (currentUser && currentUser.admin_user_id === admin_user_id && json?.data) {
        triggerProfileRefresh(json.data);
      }

      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (error) {
      setSnackbarMsg(error?.message || "Network error saving profile");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const tabLabels = ["Profile Info"];

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "25px" }}>
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}
        >
          Edit Profile
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
        >
          Back
        </Button>
      </Box>

      <Tabs value={value} onChange={handleChange}>
        {tabLabels.map((label, index) => (
          <Tab key={index} label={label} />
        ))}
      </Tabs>

      <CustomTabPanel value={value} index={0}>
        <UserInfo
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          email={email}
          setEmail={setEmail}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          password={password}
          setPassword={setPassword}
          role={role}
          setRole={setRole}
          profilePicture={profilePicture}
          setProfilePicture={setProfilePicture}
          isEdit={true}
          onValidationChange={setIsFormValid}
        />
      </CustomTabPanel>

      <Stack direction="row" alignItems="center" justifyContent="center" gap={2} mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || !isFormValid}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {loading ? "Updating..." : "Update"}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
      </Stack>

      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        autoHideDuration={3000}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Card>
  );
};

export default EditProfile;

